import apiClient from "@services/api/apiClient.jsx";

/**
 * 포토 레이아웃 관련 API 서비스
 */
const photoLayoutApi = {
    /**
     * 포토 레이아웃 목록 조회 (페이징)
     * @param {Object} params 검색 파라미터
     * @returns {Promise<Object>} 포토 레이아웃 목록
     */
    getPhotoLayouts: async (params = {}) => {
        try {
            const response = await apiClient.get('/photo-layouts', { params });
            return response.data;
        } catch (error) {
            console.error('포토 레이아웃 목록 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 모든 포토 레이아웃 조회
     * @returns {Promise<Array>} 모든 포토 레이아웃 목록
     */
    getAllPhotoLayouts: async () => {
        try {
            const response = await apiClient.get('/photo-layouts/all');
            return response.data;
        } catch (error) {
            console.error('모든 포토 레이아웃 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 포토 레이아웃 상세 조회
     * @param {number} id 포토 레이아웃 ID
     * @returns {Promise<Object>} 포토 레이아웃 상세 정보
     */
    getPhotoLayout: async (id) => {
        try {
            const response = await apiClient.get(`/photo-layouts/${id}`);
            return response.data;
        } catch (error) {
            console.error(`포토 레이아웃 ID ${id} 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 포토 레이아웃 생성
     * @param {FormData} formData 포토 레이아웃 생성 데이터
     * @returns {Promise<Object>} 생성된 포토 레이아웃 정보
     */
    createPhotoLayout: async (formData) => {
        try {
            const response = await apiClient.post('/photo-layouts', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('포토 레이아웃 생성 실패:', error);
            throw error;
        }
    },

    /**
     * 포토 레이아웃 수정
     * @param {number} id 포토 레이아웃 ID
     * @param {FormData} formData 포토 레이아웃 수정 데이터
     * @returns {Promise<Object>} 수정된 포토 레이아웃 정보
     */
    updatePhotoLayout: async (id, formData) => {
        try {
            const response = await apiClient.put(`/photo-layouts/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error(`포토 레이아웃 ID ${id} 수정 실패:`, error);
            throw error;
        }
    },

    /**
     * 포토 레이아웃 삭제
     * @param {number} id 포토 레이아웃 ID
     * @returns {Promise<Object>} 삭제 결과
     */
    deletePhotoLayout: async (id) => {
        try {
            const response = await apiClient.delete(`/photo-layouts/${id}`);
            return response.data;
        } catch (error) {
            console.error(`포토 레이아웃 ID ${id} 삭제 실패:`, error);
            throw error;
        }
    },

    /**
     * 포토 레이아웃 일괄 삭제
     * @param {Array<number>} ids 삭제할 포토 레이아웃 ID 배열
     * @returns {Promise<Object>} 삭제 결과
     */
    deletePhotoLayouts: async (ids) => {
        try {
            const response = await apiClient.delete('/photo-layouts/bulk', {
                data: ids,
            });
            return response.data;
        } catch (error) {
            console.error('포토 레이아웃 일괄 삭제 실패:', error);
            throw error;
        }
    },

    /**
     * 레이아웃명 중복 체크
     * @param {string} layoutName 레이아웃명
     * @param {number} excludeId 제외할 ID (수정 시)
     * @returns {Promise<Object>} 중복 체크 결과
     */
    checkLayoutNameDuplicate: async (layoutName, excludeId = null) => {
        try {
            const params = { layoutName };
            if (excludeId) {
                params.excludeId = excludeId;
            }
            
            const response = await apiClient.get('/photo-layouts/check-duplicate', { params });
            return response.data;
        } catch (error) {
            console.error('레이아웃명 중복 체크 실패:', error);
            throw error;
        }
    },

    /**
     * 배포 상태 토글
     * @param {number} id 포토 레이아웃 ID
     * @param {string} updatedBy 수정자
     * @returns {Promise<Object>} 수정된 포토 레이아웃 정보
     */
    toggleDeployStatus: async (id, updatedBy) => {
        try {
            const response = await apiClient.patch(`/photo-layouts/${id}/toggle-deploy`, null, {
                params: { updatedBy },
            });
            return response.data;
        } catch (error) {
            console.error(`포토 레이아웃 ID ${id} 배포 상태 변경 실패:`, error);
            throw error;
        }
    },

    /**
     * 지점별 포토 레이아웃 조회
     * @param {string} branchId 지점 ID
     * @returns {Promise<Array>} 지점별 포토 레이아웃 목록
     */
    getPhotoLayoutsByBranch: async (branchId) => {
        try {
            const response = await apiClient.get(`/photo-layouts/by-branch/${branchId}`);
            return response.data;
        } catch (error) {
            console.error(`지점 ID ${branchId}의 포토 레이아웃 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 활성 포토 레이아웃 조회
     * @returns {Promise<Array>} 활성 포토 레이아웃 목록
     */
    getActivePhotoLayouts: async () => {
        try {
            const response = await apiClient.get('/photo-layouts/active');
            return response.data;
        } catch (error) {
            console.error('활성 포토 레이아웃 조회 실패:', error);
            throw error;
        }
    },

    /**
     * FormData 생성 헬퍼 함수
     * @param {Object} data 변환할 데이터 객체
     * @returns {FormData} 생성된 FormData 객체
     */
    createFormData: (data) => {
        const formData = new FormData();
        
        // 새로운 필드들 (null 값도 전송해야 함)
        const newFields = [
            'qrX', 'qrY', 'qrSize', 
            'logoW', 'logoH', 'logoX', 'logoY', 'logoResourceId',
            'widthMm', 'heightMm', 
            'marginX', 'marginY', 'gutterX'
        ];
        
        // 기본 필드들 추가
        Object.keys(data).forEach(key => {
            if (key === 'branches' && Array.isArray(data[key])) {
                // 지점 정보는 JSON 문자열로 변환
                formData.append('branches', JSON.stringify(data[key]));
            } else if (key === 'logoFile' && data[key]) {
                // 로고 파일은 직접 추가
                formData.append('logoFile', data[key]);
            } else if (newFields.includes(key)) {
                // 새로운 필드들은 null 값도 전송 (빈 문자열로 보내면 백엔드에서 null로 처리)
                if (data[key] !== undefined) {
                    formData.append(key, data[key] === null ? '' : String(data[key]));
                }
            } else if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
                // 기존 필드들은 null, undefined, 빈 문자열이 아닌 값만 추가
                formData.append(key, String(data[key]));
            }
        });
        
        return formData;
    }
};

export default photoLayoutApi;
